import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { PenLine, Plus, Search, Loader2, Trash2, CheckCircle2 } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Ecritures() {
  const [ecritures, setEcritures] = useState([]);
  const [journaux, setJournaux] = useState([]);
  const [comptes, setComptes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [journalFilter, setJournalFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState([]);
  const [form, setForm] = useState({
    journal_code: '', date_ecriture: new Date().toISOString().split('T')[0],
    libelle: '', piece_ref: '',
    lignes: [{ compte_numero: '', libelle: '', debit: 0, credit: 0 }, { compte_numero: '', libelle: '', debit: 0, credit: 0 }],
  });

  const fetchAll = async () => {
    try {
      const [ecRes, jRes, cRes] = await Promise.all([
        axios.get(`${API_URL}/comptabilite/ecritures`, { params: { limit: 500, ...(journalFilter !== 'all' ? { journal_code: journalFilter } : {}), ...(search ? { search } : {}) } }),
        axios.get(`${API_URL}/comptabilite/journaux`),
        axios.get(`${API_URL}/comptabilite/comptes`),
      ]);
      setEcritures(ecRes.data);
      setJournaux(jRes.data);
      setComptes(cRes.data);
    } catch (e) { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { setLoading(true); fetchAll(); }, [journalFilter, search]);

  const addLine = () => setForm({ ...form, lignes: [...form.lignes, { compte_numero: '', libelle: '', debit: 0, credit: 0 }] });
  const removeLine = (i) => setForm({ ...form, lignes: form.lignes.filter((_, idx) => idx !== i) });
  const updateLine = (i, field, value) => { const l = [...form.lignes]; l[i] = { ...l[i], [field]: value }; setForm({ ...form, lignes: l }); };

  const totalDebit = form.lignes.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totalCredit = form.lignes.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  const handleCreate = async () => {
    if (!form.journal_code || !form.date_ecriture || !form.libelle) { toast.error('Champs obligatoires manquants'); return; }
    if (!isBalanced) { toast.error(`L'écriture n'est pas équilibrée (D: ${totalDebit.toFixed(2)} / C: ${totalCredit.toFixed(2)})`); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/comptabilite/ecritures`, {
        ...form,
        lignes: form.lignes.filter(l => l.compte_numero && (l.debit > 0 || l.credit > 0)),
      });
      toast.success('Écriture créée');
      setCreateOpen(false);
      setForm({ journal_code: '', date_ecriture: new Date().toISOString().split('T')[0], libelle: '', piece_ref: '', lignes: [{ compte_numero: '', libelle: '', debit: 0, credit: 0 }, { compte_numero: '', libelle: '', debit: 0, credit: 0 }] });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleValider = async () => {
    if (selected.length === 0) { toast.error('Sélectionnez des écritures'); return; }
    try {
      const res = await axios.post(`${API_URL}/comptabilite/ecritures/valider`, { ecriture_ids: selected });
      toast.success(res.data.message);
      setSelected([]);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur validation'); }
  };

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            <PenLine className="w-6 h-6 inline mr-2" />Écritures comptables
          </h1>
          <p className="text-slate-500">{ecritures.length} écriture(s)</p>
        </div>
        <div className="flex gap-2">
          {selected.length > 0 && (
            <Button variant="outline" onClick={handleValider} className="text-green-600 border-green-300">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Valider ({selected.length})
            </Button>
          )}
          <Button onClick={() => setCreateOpen(true)} className="bg-[#0F5156] hover:bg-[#0d464a]">
            <Plus className="w-4 h-4 mr-2" /> Nouvelle écriture
          </Button>
        </div>
      </div>

      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Rechercher..." className="pl-10" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Select value={journalFilter} onValueChange={setJournalFilter}>
          <SelectTrigger className="w-56"><SelectValue placeholder="Journal" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les journaux</SelectItem>
            {journaux.map(j => <SelectItem key={j.code} value={j.code}>{j.code} - {j.libelle}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead className="w-16">N°</TableHead>
                  <TableHead className="w-24">Date</TableHead>
                  <TableHead className="w-16">Journal</TableHead>
                  <TableHead>Libellé</TableHead>
                  <TableHead className="w-24">Pièce</TableHead>
                  <TableHead className="w-28 text-right">Débit</TableHead>
                  <TableHead className="w-28 text-right">Crédit</TableHead>
                  <TableHead className="w-24">Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ecritures.map(e => (
                  <TableRow key={e.id} className={selected.includes(e.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}>
                    <TableCell>
                      {!e.is_validee && <Checkbox checked={selected.includes(e.id)} onCheckedChange={() => toggleSelect(e.id)} />}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{e.numero_ecriture}</TableCell>
                    <TableCell className="text-sm">{e.date_ecriture}</TableCell>
                    <TableCell><Badge variant="outline">{e.journal_code}</Badge></TableCell>
                    <TableCell className="font-medium">{e.libelle}</TableCell>
                    <TableCell className="text-sm text-slate-500">{e.piece_ref || '-'}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(e.total_debit)}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(e.total_credit)}</TableCell>
                    <TableCell>
                      {e.is_validee ? (
                        <Badge className="bg-green-100 text-green-700">Validée</Badge>
                      ) : e.is_equilibree ? (
                        <Badge className="bg-amber-100 text-amber-700">Brouillon</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700">Déséquilibrée</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {ecritures.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center py-12 text-slate-500">Aucune écriture</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nouvelle écriture comptable</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>Journal *</Label>
                <Select value={form.journal_code} onValueChange={v => setForm({...form, journal_code: v})}>
                  <SelectTrigger><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>{journaux.map(j => <SelectItem key={j.code} value={j.code}>{j.code} - {j.libelle}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Date *</Label><Input type="date" value={form.date_ecriture} onChange={e => setForm({...form, date_ecriture: e.target.value})} /></div>
              <div><Label>Libellé *</Label><Input value={form.libelle} onChange={e => setForm({...form, libelle: e.target.value})} /></div>
              <div><Label>Réf. pièce</Label><Input value={form.piece_ref} onChange={e => setForm({...form, piece_ref: e.target.value})} /></div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base font-semibold">Lignes</Label>
                <Button variant="outline" size="sm" onClick={addLine}><Plus className="w-3 h-3 mr-1" /> Ligne</Button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-40">Compte</TableHead>
                    <TableHead>Libellé</TableHead>
                    <TableHead className="w-32">Débit</TableHead>
                    <TableHead className="w-32">Crédit</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {form.lignes.map((l, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Select value={l.compte_numero} onValueChange={v => { updateLine(i, 'compte_numero', v); const c = comptes.find(c => c.numero === v); if (c) updateLine(i, 'libelle', c.libelle); }}>
                          <SelectTrigger className="font-mono text-sm"><SelectValue placeholder="Compte" /></SelectTrigger>
                          <SelectContent className="max-h-60">{comptes.map(c => <SelectItem key={c.numero} value={c.numero}><span className="font-mono">{c.numero}</span> {c.libelle}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell><Input value={l.libelle} onChange={e => updateLine(i, 'libelle', e.target.value)} placeholder="Libellé" /></TableCell>
                      <TableCell><Input type="number" step="0.01" value={l.debit} onChange={e => updateLine(i, 'debit', parseFloat(e.target.value) || 0)} /></TableCell>
                      <TableCell><Input type="number" step="0.01" value={l.credit} onChange={e => updateLine(i, 'credit', parseFloat(e.target.value) || 0)} /></TableCell>
                      <TableCell>{form.lignes.length > 2 && <Button variant="ghost" size="sm" onClick={() => removeLine(i)}><Trash2 className="w-3 h-3 text-red-500" /></Button>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex justify-end mt-2 gap-6 text-sm">
                <span>Débit : <strong className="font-mono">{formatCurrency(totalDebit)}</strong></span>
                <span>Crédit : <strong className="font-mono">{formatCurrency(totalCredit)}</strong></span>
                <span className={isBalanced ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                  {isBalanced ? '✓ Équilibrée' : `✗ Écart : ${formatCurrency(Math.abs(totalDebit - totalCredit))}`}
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
            <Button onClick={handleCreate} disabled={saving || !isBalanced} className="bg-[#0F5156] hover:bg-[#0d464a]">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
